// Nameplate System - WoW-style floating nameplates above enemies and NPCs

import * as THREE from 'three';
import type { Enemy } from '@entities/Enemy';
import type { NPC } from '@entities/NPC';

export interface NameplateConfig {
  showHealth: boolean;
  showLevel: boolean;
  showName: boolean;
  maxDistance: number;
  fadeStartDistance: number;
}

interface NameplateData {
  element: HTMLDivElement;
  entity: Enemy | NPC;
  type: 'enemy' | 'npc';
  healthFill: HTMLDivElement | null; // PERF: Cached DOM reference
  lastHealth: number; // PERF: Track health changes
}

export class NameplateSystem {
  private container: HTMLDivElement;
  private camera: THREE.Camera;
  private nameplates: Map<string, NameplateData> = new Map();
  private config: NameplateConfig;
  private currentTarget: Enemy | null = null;

  // PERF: Reusable vectors to avoid per-frame allocations
  private readonly _tempPos: THREE.Vector3 = new THREE.Vector3();
  private readonly _tempVec: THREE.Vector3 = new THREE.Vector3();

  // PERF: Cache window dimensions
  private windowWidth: number = window.innerWidth;
  private windowHeight: number = window.innerHeight;

  // PERF: Update throttling - only update nameplates every N frames
  private updateCounter: number = 0;
  private readonly UPDATE_INTERVAL: number = 2; // Update every 2 frames

  constructor(camera: THREE.Camera, config?: Partial<NameplateConfig>) {
    this.camera = camera;
    this.config = {
      showHealth: true,
      showLevel: true,
      showName: true,
      maxDistance: 40,
      fadeStartDistance: 30,
      ...config,
    };

    // Create container for nameplates
    this.container = document.createElement('div');
    this.container.id = 'nameplate-container';
    this.container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      overflow: hidden;
      z-index: 60;
    `;
    document.getElementById('game-container')?.appendChild(this.container);

    // Toggle nameplates with 'V' key
    window.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === 'KeyV') {
        this.toggleVisibility();
      }
    });

    // PERF: Cache window size on resize
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    });
  }

  public addEnemy(enemy: Enemy): void {
    if (this.nameplates.has(enemy.id)) return;

    const element = this.createNameplate(enemy.name, enemy.level, 'enemy');
    const healthFill = element.querySelector('.nameplate-health-fill') as HTMLDivElement;

    this.nameplates.set(enemy.id, {
      element,
      entity: enemy,
      type: 'enemy',
      healthFill,
      lastHealth: enemy.health,
    });
    this.container.appendChild(element);
  }

  public addNPC(npc: NPC): void {
    const id = (npc as any).id || `npc_${Date.now()}`;
    if (this.nameplates.has(id)) return;

    const element = this.createNameplate((npc as any).name || 'NPC', 1, 'npc');
    this.nameplates.set(id, {
      element,
      entity: npc,
      type: 'npc',
      healthFill: null,
      lastHealth: 0,
    });
    this.container.appendChild(element);
  }

  public removeEntity(id: string): void {
    const data = this.nameplates.get(id);
    if (data) {
      data.element.remove();
      this.nameplates.delete(id);
    }
  }

  public setTarget(target: Enemy | null): void {
    this.currentTarget = target;
  }

  private createNameplate(name: string, level: number, type: 'enemy' | 'npc'): HTMLDivElement {
    const element = document.createElement('div');
    element.className = 'nameplate';
    element.style.cssText = `
      position: absolute;
      transform: translate(-50%, -100%);
      text-align: center;
      pointer-events: none;
      font-family: 'Segoe UI', Arial, sans-serif;
      will-change: transform, opacity;
    `;

    const nameColor = type === 'enemy' ? '#ff6666' : '#00ff00';
    const borderColor = type === 'enemy' ? '#8b0000' : '#006600';

    element.innerHTML = `
      ${this.config.showLevel ? `
        <div class="nameplate-level" style="
          display: inline-block;
          background: ${borderColor};
          color: #fff;
          font-size: 10px;
          font-weight: bold;
          width: 16px;
          height: 16px;
          line-height: 16px;
          border-radius: 50%;
          margin-bottom: 2px;
        ">${level}</div>
      ` : ''}
      ${this.config.showName ? `
        <div class="nameplate-name" style="
          color: ${nameColor};
          font-size: 11px;
          font-weight: bold;
          text-shadow: 1px 1px 2px #000, -1px -1px 2px #000;
          white-space: nowrap;
          margin-bottom: 2px;
        ">${name}</div>
      ` : ''}
      ${this.config.showHealth && type === 'enemy' ? `
        <div class="nameplate-health-bg" style="
          width: 80px;
          height: 8px;
          background: #333;
          border: 1px solid ${borderColor};
          border-radius: 3px;
          overflow: hidden;
          margin: 0 auto;
        ">
          <div class="nameplate-health-fill" style="
            width: 100%;
            height: 100%;
            background: linear-gradient(180deg, #44ff44, #22aa22);
          "></div>
        </div>
      ` : ''}
    `;

    return element;
  }

  public update(): void {
    // PERF: Throttle updates - skip frames for better performance
    this.updateCounter++;
    if (this.updateCounter < this.UPDATE_INTERVAL) {
      return;
    }
    this.updateCounter = 0;

    const camPos = this.camera.position;
    const maxDistSq = this.config.maxDistance * this.config.maxDistance;
    const fadeStartDistSq = this.config.fadeStartDistance * this.config.fadeStartDistance;

    for (const [id, data] of this.nameplates) {
      const { element, entity, type, healthFill } = data;

      // Check if entity still exists and is alive (for enemies)
      if (type === 'enemy') {
        const enemy = entity as Enemy;
        if (!enemy.isAlive()) {
          element.style.display = 'none';
          continue;
        }
      }

      // Get entity position - avoid clone, use mesh position directly
      const entityPos = (entity as Enemy).mesh?.position || (entity as any).mesh?.position;
      if (!entityPos) {
        element.style.display = 'none';
        continue;
      }

      // PERF: Use squared distance to avoid sqrt
      const dx = entityPos.x - camPos.x;
      const dy = entityPos.y - camPos.y;
      const dz = entityPos.z - camPos.z;
      const distSq = dx * dx + dy * dy + dz * dz;

      // Hide if too far
      if (distSq > maxDistSq) {
        element.style.display = 'none';
        continue;
      }

      // Calculate screen position - reuse vectors
      this._tempPos.set(entityPos.x, entityPos.y + 2.2, entityPos.z);
      this._tempVec.copy(this._tempPos).project(this.camera);

      // Check if in front of camera
      if (this._tempVec.z > 1) {
        element.style.display = 'none';
        continue;
      }

      const x = (this._tempVec.x * 0.5 + 0.5) * this.windowWidth;
      const y = (-this._tempVec.y * 0.5 + 0.5) * this.windowHeight;

      // Check if on screen
      if (x < -100 || x > this.windowWidth + 100 || y < -100 || y > this.windowHeight + 100) {
        element.style.display = 'none';
        continue;
      }

      element.style.display = 'block';
      element.style.left = `${x}px`;
      element.style.top = `${y}px`;

      // Apply distance-based opacity
      let opacity = 1;
      if (distSq > fadeStartDistSq) {
        const dist = Math.sqrt(distSq);
        opacity = 1 - (dist - this.config.fadeStartDistance) /
          (this.config.maxDistance - this.config.fadeStartDistance);
      }

      // Highlight current target
      const isTarget = this.currentTarget && (this.currentTarget as Enemy).id === id;
      if (isTarget) {
        element.style.transform = 'translate(-50%, -100%) scale(1.1)';
        opacity = 1;
      } else {
        element.style.transform = 'translate(-50%, -100%)';
      }

      element.style.opacity = opacity.toString();

      // PERF: Only update health bar if health changed
      if (type === 'enemy' && healthFill) {
        const enemy = entity as Enemy;
        if (enemy.health !== data.lastHealth) {
          data.lastHealth = enemy.health;
          const healthPercent = (enemy.health / enemy.maxHealth) * 100;
          healthFill.style.width = `${healthPercent}%`;

          // Color based on health
          if (healthPercent > 50) {
            healthFill.style.background = 'linear-gradient(180deg, #44ff44, #22aa22)';
          } else if (healthPercent > 25) {
            healthFill.style.background = 'linear-gradient(180deg, #ffff44, #aaaa22)';
          } else {
            healthFill.style.background = 'linear-gradient(180deg, #ff4444, #aa2222)';
          }
        }
      }
    }
  }

  private toggleVisibility(): void {
    this.container.style.display = this.container.style.display === 'none' ? 'block' : 'none';
  }

  public destroy(): void {
    this.container.remove();
  }
}
